import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import { AppComponent } from './app.component';

import {
  NbThemeModule,
  NbSidebarService,
  NbRouteTabsetModule,
  NbInputModule,
  NbButtonModule,
  NbCardModule,
  NbListModule,
  NbAccordionModule,
  NbPopoverModule,
  NbProgressBarModule,
  NbDialogModule,
  NbMenuModule,
  NbSidebarModule,
  NbLayoutModule,
  NbToggleModule,
  NbStepperModule,
  NbActionsModule,
  NbBadgeModule,
  NbToastrModule,
  NbGlobalPhysicalPosition,
  NbSpinnerModule,
  NbAutocompleteModule,
  NbSelectModule,
  NbContextMenuModule,
  NbIconModule,
  NbTooltipModule,
  NbCheckboxModule,
  NbFormFieldModule,
  NbTabsetModule,
} from '@nebular/theme';
import { RouterModule, Routes, RouteReuseStrategy } from '@angular/router';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { HomeComponent } from './home/home.component';
import { NavigationComponent } from './home/navigation/navigation.component';
import { AuthComponent } from './auth/auth.component';
import { environment } from 'src/environments/environment';
import { AngularFireModule } from '@angular/fire/compat';
import { LoginComponent } from './auth/login/login.component';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AngularFirestoreModule } from '@angular/fire/compat/firestore';
import { AngularFireAuthModule } from '@angular/fire/compat/auth';
import { AngularFireStorageModule } from '@angular/fire/compat/storage';
import { ProfileComponent } from './home/profile/profile.component';
import { AuthService } from './shared/services/auth.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DropZoneDirective } from './shared/directives/dropzone.directive';
import { RegisterComponent } from './auth/register/register.component';
import { ExplainerComponent } from './shared/ui/explainer/explainer.component';
import { WelcomeComponent } from './home/welcome/welcome.component';
import { ConvertComponent } from './home/convert/convert.component';
import { Convert1Component } from './home/convert/convert-1/convert-1.component';
import { Convert2Component } from './home/convert/convert-2/convert-2.component';
import { Convert3Component } from './home/convert/convert-3/convert-3.component';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { HistoryComponent } from './home/history/history.component';
import { ConversionRowComponent } from './shared/ui/conversion-row/conversion-row.component';
import { ConversionBatchHeaderComponent } from './shared/ui/conversion-row/conversion-batch-header.component';
import { InfiniteScrollTriggerDirective } from './shared/directives/infinite-scroll-trigger.directive';
import { ConversionDetailsComponent } from './shared/ui/conversion-details/conversion-details.component';
import { AddCreditsComponent } from './shared/ui/add-credits/add-credits.component';
import { CardDetailsComponent } from './shared/ui/card-details/card-details.component';
import { PaymentHandlerComponent } from './home/payment-handler/payment-handler.component';
import { DialogComponent } from './shared/ui/dialog/dialog.component';
import { CustomRouteReuseStrategy } from './shared/custom-route-reuse-strategy';
import { DummyComponent } from './shared/dummy.component';
import { ContactUsComponent } from './shared/ui/contact-us/contact-us.component';
import { PricingPlanComponent } from './shared/ui/pricing-plan/pricing-plan.component';
import { ResetPasswordComponent } from './auth/reset-password/reset-password.component';
import { EftPaymentSuccessHandlerComponent } from './home/eft-payment-success-handler/eft-payment-success-handler.component';
import { CountrySelectComponent } from './shared/ui/country-select/country-select.component';
import { PurchaseRowComponent } from './shared/ui/purchase-row/purchase-row.component';
import { PurchaseHistoryComponent } from './home/purchase-history/purchase-history.component';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { AddPromotionComponent } from './shared/ui/add-promotion/add-promotion.component';
import { NgModelChangeDebouncedDirective } from './shared/directives/debounced-model-change.directive';
import { SplashComponent } from './home/splash/splash.component';
import { XeroButtonComponent } from './home/xero/xero-button/xero-button.component';
import { XeroAuthService } from './shared/services/xero-auth.service';
import { ProfileXeroComponent } from './home/profile/xero/profile-xero.component';
import { ProfileCompletionComponent } from './home/profile-completion/profile-completion.component';
import { ProfileCompletionGuard } from './shared/guards/profile-completion.guard';
import { ProfileMainComponent } from './home/profile/main/profile-main.component';
import { ProfileNotificationsComponent } from './home/profile/notifications/profile-notifications.component';
import { XeroCallbackComponent } from './home/xero/xero-callback/xero-callback.component';
import { XeroApiService } from './shared/services/xero-api.service';
import { XeroUserService } from './shared/services/xero-user.service';
import { AuthInterceptor } from './shared/interceptors/auth.interceptor';
import { XeroAutoRedirectComponent } from './home/xero/xero-auto-redirect/xero-auto-redirect.component';
import { XeroFeedConnectionsComponent } from './shared/ui/xero-feed-connections/xero-feed-connections.component';

const routes: Routes = [
  {
    path: 'auth',
    component: AuthComponent,
    children: [
      {
        path: 'login',
        component: LoginComponent,
      },
      {
        path: 'register',
        component: RegisterComponent,
      },
      {
        path: 'reset-password',
        component: ResetPasswordComponent,
      },
      {
        path: 'profile-complete',
        component: ProfileCompletionComponent,
      },
      {
        path: 'xero-signin',
        component: XeroAutoRedirectComponent,
      },
      {
        path: 'oauth',
        children: [
          {
            path: 'xero/redirect',
            component: XeroCallbackComponent,
          },
        ],
      },
    ],
  },
  {
    path: 'home',
    component: HomeComponent,
    canActivate: [AuthService, ProfileCompletionGuard],
    children: [
      { path: 'dummy', component: DummyComponent },
      {
        path: 'convert',
        component: ConvertComponent,
      },
      {
        path: 'payment/:currencyCode/:paymentPlanUid',
        component: PaymentHandlerComponent,
      },
      {
        path: 'eftPayment/:numOfCreditsPurchased',
        component: EftPaymentSuccessHandlerComponent,
      },
      {
        path: 'welcome',
        component: WelcomeComponent,
      },
      {
        path: 'profile',
        component: ProfileComponent,
        children: [
          {
            path: '',
            redirectTo: 'main',
            pathMatch: 'full',
          },
          {
            path: 'main',
            component: ProfileMainComponent,
          },
          {
            path: 'notifications',
            component: ProfileNotificationsComponent,
          },
          {
            path: 'xero',
            component: ProfileXeroComponent,
          },
          {
            path: 'add-promotion',
            component: ProfileComponent,
          },
        ],
      },
      {
        path: 'purchases',
        component: PurchaseHistoryComponent,
      },
      {
        path: 'purchases/:purchaseUid',
        component: PurchaseHistoryComponent,
      },
      {
        path: 'history',
        component: HistoryComponent,
      },
      {
        path: 'history/:conversionUid',
        component: HistoryComponent,
      },
    ],
  },
  {
    path: '',
    redirectTo: 'home/convert',
    pathMatch: 'full',
  },
  { path: '**', redirectTo: 'home/convert' },
];

@NgModule({
  declarations: [
    AddCreditsComponent,
    AddPromotionComponent,
    AppComponent,
    AuthComponent,
    CardDetailsComponent,
    ContactUsComponent,
    ConversionDetailsComponent,
    Convert1Component,
    Convert2Component,
    Convert3Component,
    ConvertComponent,
    CountrySelectComponent,
    DialogComponent,
    DropZoneDirective,
    DummyComponent,
    EftPaymentSuccessHandlerComponent,
    ExplainerComponent,
    HistoryComponent,
    HomeComponent,
    LoginComponent,
    NavigationComponent,
    NgModelChangeDebouncedDirective,
    PaymentHandlerComponent,
    PricingPlanComponent,
    ProfileComponent,
    ProfileMainComponent,
    ProfileNotificationsComponent,
    ProfileXeroComponent,
    ProfileCompletionComponent,
    PurchaseHistoryComponent,
    PurchaseRowComponent,
    RegisterComponent,
    ResetPasswordComponent,
    SplashComponent,
    WelcomeComponent,
    XeroAutoRedirectComponent,
    XeroButtonComponent,
    XeroCallbackComponent,
    XeroFeedConnectionsComponent,
  ],
  imports: [
    BrowserModule,
    ConversionRowComponent,
    ConversionBatchHeaderComponent,
    InfiniteScrollTriggerDirective,
    RouterModule.forRoot(routes),
    AngularFireModule.initializeApp(environment.firebase),
    NbThemeModule.forRoot({ name: 'default' }),
    NbDialogModule.forRoot(),
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbToastrModule.forRoot({ position: NbGlobalPhysicalPosition.TOP_RIGHT, hasIcon: true, duration: 4000 }),
    NbContextMenuModule,
    NbTabsetModule,
    NgCircleProgressModule.forRoot({
      outerStrokeWidth: 24,
      innerStrokeWidth: 8,
      outerStrokeColor: '#40A3B0',
      innerStrokeColor: '#93CCE0',
      outerStrokeLinecap: 'square',
    }),
    NbSpinnerModule,
    NbAutocompleteModule,
    NbTooltipModule,
    NbLayoutModule,
    NbToggleModule,
    AngularFireAuthModule,
    AngularFirestoreModule,
    AngularFireStorageModule,
    NbBadgeModule,
    NbEvaIconsModule,
    NbIconModule,
    NbSelectModule,
    NbRouteTabsetModule,
    FormsModule,
    ReactiveFormsModule,
    NbCheckboxModule,
    NbInputModule,
    NbFormFieldModule,
    NbButtonModule,
    NbCardModule,
    NbListModule,
    NbActionsModule,
    NbAccordionModule,
    NbPopoverModule,
    NbProgressBarModule,
    BrowserAnimationsModule,
    NbStepperModule,
    HttpClientModule,
    ScrollingModule,
  ],
  providers: [
    NbSidebarService,
    XeroAuthService,
    XeroApiService,
    XeroUserService,
    AuthService,
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategy,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
